import DoorImage from './DoorImage';
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, MessageCircle, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";
import { useAuth } from "../lib/auth";
import { useLang, localized } from "../lib/i18n";
import { useSettings, whatsappUrl } from "../lib/settings";
import { api, FILE_URL } from "../lib/api";

const DEFAULT_DOOR = "https://images.unsplash.com/photo-1659720879327-827462ca3942?crop=entropy&cs=srgb&fm=jpg&q=85&w=400";

export default function CartDrawer({ open, onOpenChange }) {
  const { t, lang } = useLang();
  const { user, cart, setCartQty, removeFromCart, clearCart } = useAuth();
  const { settings } = useSettings();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const cartKey = cart.map((c) => c.door_id + ":" + c.qty).join(",");

  useEffect(() => {
    if (!open) return;
    let active = true;
    (async () => {
      setLoading(true);
      try {
        if (cart.length === 0) { if (active) setItems([]); return; }
        if (user) {
          const r = await api.get("/me/cart");
          if (active) setItems(r.data);
        } else {
          const all = await api.get("/doors").then((r) => r.data);
          const byId = Object.fromEntries(all.map((d) => [d.id, d]));
          if (active) setItems(cart.map((c) => ({ door: byId[c.door_id], qty: c.qty })).filter((x) => x.door));
        }
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [open, user, cartKey]);

  const total = items.reduce((s, it) => s + (Number(it.door.price) || 0) * it.qty, 0);
  const count = items.reduce((s, it) => s + it.qty, 0);

  const buildMessage = () => {
    const lines = [t("cart.wa_greeting")];
    items.forEach((it, i) => {
      lines.push(`${i + 1}. ${localized(it.door.name, lang)} — ${it.qty} × ${it.door.price} ${t("common.currency")}`);
    });
    lines.push("");
    lines.push(`${t("cart.total")}: ${total} ${t("common.currency")}`);
    if (user) lines.push(`${t("auth.name")}: ${user.name} ${user.surname}`);
    return lines.join("\n");
  };
  const orderWa = () => {
    if (items.length === 0) return;
    window.open(whatsappUrl(settings && settings.whatsapp, buildMessage()), "_blank");
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:w-1/2 sm:max-w-none p-0 flex flex-col" data-testid="cart-drawer">
        <SheetTitle className="sr-only">{t("auth.cart")}</SheetTitle>
        <div className="flex items-center justify-between p-5 border-b border-neutral-200">
          <div className="flex items-center gap-2">
            <ShoppingBag size={18} />
            <h2 className="text-lg text-neutral-900">{t("auth.cart")}</h2>
            {count > 0 && <span className="text-sm text-neutral-400">({count})</span>}
          </div>
        </div>

        <div className="flex-1 overflow-auto p-5">
          {loading ? (
            <p className="text-sm text-neutral-400">{t("common.loading")}</p>
          ) : items.length === 0 ? (
            <div className="py-20 text-center text-sm text-neutral-400" data-testid="cart-drawer-empty">
              {t("cart.empty")}
              <Link to="/catalog" onClick={() => onOpenChange(false)} className="text-neutral-900 underline ml-1">{t("nav.catalog")}</Link>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map(({ door, qty }) => (
                <div key={door.id} className="flex gap-3 border border-neutral-200 p-3" data-testid={`cart-drawer-item-${door.id}`}>
                  <Link to={`/doors/${door.id}`} onClick={() => onOpenChange(false)} className="w-16 h-20 bg-neutral-100 overflow-hidden shrink-0">
                    <DoorImage src={door.image_id ? FILE_URL(door.image_id) : DEFAULT_DOOR} alt="" fallback={DEFAULT_DOOR} />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/doors/${door.id}`} onClick={() => onOpenChange(false)} className="text-sm text-neutral-900 hover:underline block truncate">{localized(door.name, lang)}</Link>
                    <p className="text-xs text-neutral-500 mt-0.5">{door.price} {t("common.currency")} × {qty}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <div className="inline-flex items-center border border-neutral-300">
                        <button onClick={() => setCartQty(door.id, Math.max(1, qty - 1))} className="w-7 h-7 flex items-center justify-center hover:bg-neutral-100" aria-label="decrease" data-testid={`cart-drawer-dec-${door.id}`}><Minus size={12} /></button>
                        <span className="px-2 text-sm min-w-[28px] text-center" data-testid={`cart-drawer-qty-${door.id}`}>{qty}</span>
                        <button onClick={() => setCartQty(door.id, qty + 1)} className="w-7 h-7 flex items-center justify-center hover:bg-neutral-100" aria-label="increase" data-testid={`cart-drawer-inc-${door.id}`}><Plus size={12} /></button>
                      </div>
                      <button onClick={() => removeFromCart(door.id)} className="text-xs text-neutral-500 hover:text-red-600 inline-flex items-center gap-1" data-testid={`cart-drawer-remove-${door.id}`}>
                        <Trash2 size={12} /> {t("auth.remove")}
                      </button>
                    </div>
                  </div>
                  <div className="text-sm text-neutral-900 shrink-0">{door.price * qty} {t("common.currency")}</div>
                </div>
              ))}
              <button onClick={() => { if (window.confirm(t("cart.confirm_clear"))) clearCart(); }} className="text-xs text-neutral-500 hover:text-red-600 mt-2" data-testid="cart-drawer-clear">
                {t("cart.clear")}
              </button>
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-neutral-200 p-5 space-y-4">
            <div className="flex justify-between text-lg text-neutral-900">
              <span>{t("cart.total")}</span>
              <span>{total} {t("common.currency")}</span>
            </div>
            <button onClick={orderWa} className="fd-btn-primary w-full justify-center !bg-[#25D366] hover:!bg-[#1ebe57]" data-testid="cart-drawer-whatsapp">
              <MessageCircle size={16} /> {t("cart.order_wa")}
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
